import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../data/supabaseClient';
import { CustomInputBox1, CustomButton2, CustomButton10Ca } from '../Templates';
import Spinner from '../common/Spinner';
import './ProfileComplete.css';

// Validation constants for car details
const BRAND_MIN_LENGTH = 2;
const BRAND_MAX_LENGTH = 30;
const MODEL_MAX_LENGTH = 50;
const LICENSE_PLATE_MIN_LENGTH = 2;
const LICENSE_PLATE_MAX_LENGTH = 15;
const BRAND_REGEX = /^[a-zA-Z0-9\s\-\.]+$/; // Allow letters, numbers, spaces, hyphens, dots
const LICENSE_PLATE_REGEX = /^[a-zA-Z0-9\s\-]+$/; // Allow letters, numbers, spaces, hyphens

const Step3CarDetails = ({ userData, onComplete, onSkip }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: '',
    number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  // Gate: only show the car form to users who say they'll offer rides. Others
  // skip it (car details are pointless for passengers / package-only users).
  const [wantsToDrive, setWantsToDrive] = useState(null); // null = not chosen yet

  // Validation functions
  const validateBrand = (brand) => {
    if (!brand || brand.trim().length === 0) {
      return null; // Brand is optional unless number is provided
    }
    
    const trimmedBrand = brand.trim();
    
    if (trimmedBrand.length < BRAND_MIN_LENGTH) {
      return t('errors.car_brand_too_short', { min: BRAND_MIN_LENGTH });
    }
    
    if (trimmedBrand.length > BRAND_MAX_LENGTH) {
      return t('errors.car_brand_too_long', { max: BRAND_MAX_LENGTH });
    }
    
    if (!BRAND_REGEX.test(trimmedBrand)) {
      return t('errors.car_brand_invalid_characters');
    }
    
    return null;
  };

  const validateModel = (model) => {
    if (!model || model.trim().length === 0) {
      return null; // Model is optional
    }
    
    const trimmedModel = model.trim();
    
    if (trimmedModel.length > MODEL_MAX_LENGTH) {
      return t('errors.car_model_too_long', { max: MODEL_MAX_LENGTH });
    }
    
    if (!BRAND_REGEX.test(trimmedModel)) {
      return t('errors.car_model_invalid_characters');
    }
    
    return null;
  };

  const validateLicensePlate = (licensePlate) => {
    if (!licensePlate || licensePlate.trim().length === 0) {
      return null; // License plate is optional unless brand is provided
    }
    
    const trimmedPlate = licensePlate.trim();
    
    if (trimmedPlate.length < LICENSE_PLATE_MIN_LENGTH) {
      return t('errors.license_plate_too_short', { min: LICENSE_PLATE_MIN_LENGTH });
    }
    
    if (trimmedPlate.length > LICENSE_PLATE_MAX_LENGTH) {
      return t('errors.license_plate_too_long', { max: LICENSE_PLATE_MAX_LENGTH });
    }
    
    if (!LICENSE_PLATE_REGEX.test(trimmedPlate)) {
      return t('errors.license_plate_invalid_characters');
    }
    
    return null;
  };

  const validateYear = (year) => {
    if (!year) {
      return null; // Year is optional
    }
    
    const currentYear = new Date().getFullYear();
    const yearNum = parseInt(year);
    
    if (yearNum < 1900 || yearNum > currentYear + 1) {
      return t('errors.invalid_car_year');
    }
    
    return null;
  };

  // Input change handler with validation
  const handleInputChange = (field, value) => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear previous validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [field]: null
    }));

    // Validate on change for immediate feedback
    let validationError = null;
    
    switch (field) {
      case 'brand':
        validationError = validateBrand(value);
        break;
      case 'model':
        validationError = validateModel(value);
        break;
      case 'number':
        validationError = validateLicensePlate(value);
        break;
      case 'year':
        validationError = validateYear(value);
        break;
      default:
        break;
    }

    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: validationError
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Check if any car field is filled
    const hasAnyCarData = formData.brand?.trim() || formData.model?.trim() || formData.year || formData.number?.trim();
    
    if (hasAnyCarData) {
      // If ANY car field is filled, validate all fields and require brand + number
      
      // Validate individual fields
      const brandError = validateBrand(formData.brand);
      if (brandError) errors.brand = brandError;
      
      const modelError = validateModel(formData.model);
      if (modelError) errors.model = modelError;
      
      const numberError = validateLicensePlate(formData.number);
      if (numberError) errors.number = numberError;
      
      const yearError = validateYear(formData.year);
      if (yearError) errors.year = yearError;
      
      // Cross-field validation: If ANY field filled, require brand AND number
      if (!formData.brand || !formData.brand.trim()) {
        errors.brand = t('profile_setup.car_brand_required');
      }
      
      if (!formData.number || !formData.number.trim()) {
        errors.number = t('profile_setup.car_number_required');
      }
    }
    
    // Update validation errors state
    setValidationErrors(errors);
    
    return { isValid: Object.keys(errors).length === 0, errors, hasAnyCarData };
  };

  // Check if we have complete car data (for enabling Next button)
  const hasCompleteCarData = useMemo(() => {
    if (!formData.brand?.trim() || !formData.number?.trim()) {
      return false;
    }
    
    // Basic validation without triggering validateForm
    const brandValid = formData.brand.trim().length >= BRAND_MIN_LENGTH && 
                      formData.brand.trim().length <= BRAND_MAX_LENGTH &&
                      BRAND_REGEX.test(formData.brand.trim());
                      
    const numberValid = formData.number.trim().length >= LICENSE_PLATE_MIN_LENGTH &&
                       formData.number.trim().length <= LICENSE_PLATE_MAX_LENGTH &&
                       LICENSE_PLATE_REGEX.test(formData.number.trim());
                       
    return brandValid && numberValid;
  }, [formData.brand, formData.number]);

  // Handle skipping car completely
  const handleSkipCar = async () => {
    setLoading(true);
    setError(null);

    try {
      // Clear any partial car data and complete profile without car
      const profileData = {
        has_car: false
      };

      await onComplete(profileData);
    } catch (error) {
      console.error('Error completing profile without car:', error);
      setError(error.message || t('errors.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only proceed if we have complete car data
    if (!hasCompleteCarData) {
      setError(t('profile_setup.complete_car_details_required'));
      return;
    }
    
    // Validate form
    const validation = validateForm();
    if (!validation.isValid) {
      setError(t('errors.please_fix_errors'));
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      // Add car since we have complete valid data
      const carData = {
        user_id: userData.id,
        brand: formData.brand.trim(),
        model: formData.model ? formData.model.trim() : null,
        year: formData.year ? parseInt(formData.year) : null,
        number: formData.number.trim().toUpperCase() // Normalize license plate to uppercase
      };

      // Check if license plate already exists (optional validation)
      const { data: existingCar, error: checkError } = await supabase
        .from('cars')
        .select('id')
        .eq('number', carData.number)
        .neq('user_id', userData.id)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        console.warn('Could not check license plate uniqueness:', checkError);
        // Continue anyway - this is just a warning check
      }

      if (existingCar) {
        setValidationErrors(prev => ({
          ...prev,
          number: t('errors.license_plate_already_exists')
        }));
        setError(t('errors.license_plate_already_exists'));
        return;
      }

      // Add car to cars table
      const { error: carError } = await supabase
        .from('cars')
        .insert([carData]);

      if (carError) {
        console.error('Error saving car:', carError);
        throw new Error(carError.message || t('errors.save_car_failed'));
      }

      console.log('Car information saved successfully');

      // Complete profile setup with car
      const profileData = {
        has_car: true
      };

      await onComplete(profileData);

    } catch (error) {
      console.error('Error saving car details:', error);
      setError(error.message || t('errors.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Generate years for the dropdown (cars from 1990 to next year)
  const currentYear = new Date().getFullYear();
  const startYear = 1990;
  const endYear = currentYear + 1; // Allow next year for pre-release models
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);

  return (
    <div className="pcf-form-step">
      <div className="pcf-car-details-intro">
        <small className="pcf-helper-text">
          {t('profile_setup.car_details_help')}
        </small>
      </div>

      {wantsToDrive === null ? (
        <div className="pcf-content-container pcf-drive-gate">
          <h4 className="pcf-drive-question">{t('profile_setup.drive_question')}</h4>
          {error && <div className="pcf-error-text">{error}</div>}
          <div className="pcf-button-group">
            <button
              type="button"
              className="enhanced-profile-button secondary"
              onClick={handleSkipCar}
              disabled={loading}
            >
              {loading ? (
                <div className="button-label-with-spinner">
                  <Spinner size="small" color="var(--light-color)" showText={false} />
                  <span>{t('app.loading')}</span>
                </div>
              ) : (
                t('profile_setup.drive_no')
              )}
            </button>
            <button
              type="button"
              className="enhanced-profile-button primary"
              onClick={() => setWantsToDrive(true)}
              disabled={loading}
            >
              {t('profile_setup.drive_yes')}
            </button>
          </div>
        </div>
      ) : (
      <div className="pcf-content-container">
        <form onSubmit={handleSubmit}>
          <div className="pcf-input-group">
            <div className="pcf-form-field">
              <label>{t('profile.car_brand')}</label>
              <input
                className="enhanced-profile-input"
                value={formData.brand}
                onChange={(e) => handleInputChange('brand', e.target.value)}
                placeholder={t('profile.car_brand')}
                disabled={loading}
                maxLength={BRAND_MAX_LENGTH}
                required={false}
              />
              {validationErrors.brand && (
                <div className="pcf-error-text">{validationErrors.brand}</div>
              )}
            </div>

            <div className="pcf-form-field">
              <label>{t('profile.car_model')}</label>
              <input
                className="enhanced-profile-input"
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                placeholder={t('profile.car_model')}
                disabled={loading}
                maxLength={MODEL_MAX_LENGTH}
                required={false}
              />
              {validationErrors.model && (
                <div className="pcf-error-text">{validationErrors.model}</div>
              )}
            </div>

            <div className="pcf-form-field">
              <label>{t('profile.plate_number')}</label>
              <input
                className="enhanced-profile-input"
                value={formData.number}
                onChange={(e) => handleInputChange('number', e.target.value.toUpperCase())}
                placeholder={t('profile.plate_number')}
                disabled={loading}
                maxLength={LICENSE_PLATE_MAX_LENGTH}
                required={false}
              />
              {validationErrors.number && (
                <div className="pcf-error-text">{validationErrors.number}</div>
              )}
              <small className="pcf-helper-text">
                {t('profile_setup.license_plate_help')}
              </small>
            </div>
          </div>

          <div className="pcf-form-field">
            <label>{t('profile_setup.select_car_year')}</label>
            <select
              className="pcf-form-select"
              value={formData.year}
              onChange={(e) => handleInputChange('year', e.target.value)}
              disabled={loading}
            >
              <option value="">{t('profile_setup.select_car_year')}</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {validationErrors.year && (
              <div className="pcf-error-text">{validationErrors.year}</div>
            )}
          </div>

          {error && <div className="pcf-error-text">{error}</div>}

          <div className="pcf-button-group">
            <button
              type="button"
              className="enhanced-profile-button secondary"
              onClick={handleSkipCar}
              disabled={loading}
            >
              {loading ? (
                <div className="button-label-with-spinner">
                  <Spinner size="small" color="var(--light-color)" showText={false} />
                  <span>{t('app.loading')}</span>
                </div>
              ) : (
                t('buttons.skip_car')
              )}
            </button>
            <button
              type="submit"
              className={`enhanced-profile-button primary ${!hasCompleteCarData ? 'pcf-button-disabled' : ''}`}
              disabled={loading || !hasCompleteCarData}
            >
              {loading ? (
                <div className="button-label-with-spinner">
                  <Spinner size="small" color="var(--background-color)" showText={false} />
                  <span>{t('app.loading')}</span>
                </div>
              ) : (
                t('buttons.next')
              )}
            </button>
          </div>
        </form>
      </div>
      )}
    </div>
  );
};

export default Step3CarDetails;